import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Card, Button, Table, Tag, message, Select, Space, Modal, Progress, Pagination, Upload, Radio } from 'antd';
import { PlayCircleOutlined, DownloadOutlined, VideoCameraOutlined, ReloadOutlined, DeleteOutlined, ImportOutlined, ExportOutlined, FileExcelOutlined, FileTextOutlined, UploadOutlined, ClusterOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { shotApi, assetApi, aiApi, projectApi, api } from '../services/api';

const { Option } = Select;

const ProductionPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [shots, setShots] = useState([]);
  const [assets, setAssets] = useState([]);
  const [episodes, setEpisodes] = useState([]);
  const [activeEpisode, setActiveEpisode] = useState('all');
  const [loading, setLoading] = useState(false);
  const [generatingVideo, setGeneratingVideo] = useState(null);
  const [project, setProject] = useState(null);

  // 视频生成确认对话框状态
  const [videoConfirmVisible, setVideoConfirmVisible] = useState(false);
  const [currentShot, setCurrentShot] = useState(null);
  const [videoConfig, setVideoConfig] = useState({
    prompt: '',
    aspectRatio: '16:9',
    duration: '5',
    resolution: '720P',
    referenceSource: 'none',  // 'none' | 'asset' | 'local'
    referenceAssetIds: [],  // 从资产库选择的参考资产ID列表
    referenceImage: null,  // 本地上传的参考图
    referenceVideoUrl: '',
    referenceAudioUrl: ''
  });
  const [videoProgress, setVideoProgress] = useState({
    visible: false,
    percent: 0,
    status: ''
  });

  // 选中的分镜组（用于批量删除）
  const [selectedGroups, setSelectedGroups] = useState([]);

  // 分页状态
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });

  // 导入导出分镜组相关状态
  const [isImportGroupsModalVisible, setIsImportGroupsModalVisible] = useState(false);
  const [isExportGroupsModalVisible, setIsExportGroupsModalVisible] = useState(false);
  const [importGroupsFile, setImportGroupsFile] = useState(null);

  // 分镜组规划状态
  const [isGeneratingShotGroups, setIsGeneratingShotGroups] = useState(false);
  const [isShotGroupsModalVisible, setIsShotGroupsModalVisible] = useState(false);
  const [shotGroupsProgress, setShotGroupsProgress] = useState({ current: 0, total: 0, episode_title: '' });

  // Refs
  const shotGroupsIntervalRef = useRef(null);

  // 加载分镜和资产
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // 加载项目信息以获取画面比例设置
      const projectResponse = await projectApi.getById(id);
      setProject(projectResponse.data);

      // 加载分镜（现在分镜已经包含 group_id 字段）
      const shotsResponse = await shotApi.getAll(id);
      let shotsData = shotsResponse.data || [];

      // 自动从提示词中提取 @人物、$道具、#场景
      shotsData = shotsData.map(shot => {
        const extracted = extractElementsFromPrompt(shot.prompt);
        return {
          ...shot,
          involved_characters: shot.involved_characters || extracted.characters.join(','),
          involved_props: shot.involved_props || extracted.props.join(','),
          involved_scenes: shot.involved_scenes || extracted.scenes.join(',')
        };
      });

      // 对分镜进行排序：先按 group_id 排序，再按 group_order 排序，最后按 index 排序
      shotsData.sort((a, b) => {
        // 首先按 group_id 排序
        if (a.group_id !== b.group_id) {
          return (a.group_id || 0) - (b.group_id || 0);
        }
        // 然后按 group_order 排序
        if (a.group_order !== b.group_order) {
          return (a.group_order || 0) - (b.group_order || 0);
        }
        // 最后按 index 排序
        return (a.index || 0) - (b.index || 0);
      });

      setShots(shotsData);

      // 提取所有集数
      const episodeSet = new Set();
      shotsData.forEach(shot => {
        if (shot.episode) {
          episodeSet.add(shot.episode);
        }
      });
      const episodeList = Array.from(episodeSet).sort();
      setEpisodes(episodeList);

      // 加载资产
      const assetsResponse = await assetApi.getAll(id);
      setAssets(assetsResponse.data || []);
    } catch (error) {
      message.error('加载数据失败');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      loadData();
    }
  }, [id, loadData]);

  // 从提示词中提取 @人物、$道具、#场景
  const extractElementsFromPrompt = (prompt) => {
    if (!prompt) return { characters: [], props: [], scenes: [] };

    const characters = [];
    const props = [];
    const scenes = [];

    // 提取 @人物
    const charMatches = prompt.match(/@([^\s@#$]+)/g);
    if (charMatches) {
      charMatches.forEach(match => {
        const name = match.substring(1); // 去掉 @
        if (!characters.includes(name)) {
          characters.push(name);
        }
      });
    }

    // 提取 $道具
    const propMatches = prompt.match(/\$([^\s@#$]+)/g);
    if (propMatches) {
      propMatches.forEach(match => {
        const name = match.substring(1); // 去掉 $
        if (!props.includes(name)) {
          props.push(name);
        }
      });
    }

    // 提取 #场景
    const sceneMatches = prompt.match(/#([^\s@#$]+)/g);
    if (sceneMatches) {
      sceneMatches.forEach(match => {
        const name = match.substring(1); // 去掉 #
        if (!scenes.includes(name)) {
          scenes.push(name);
        }
      });
    }

    return { characters, props, scenes };
  };

  // 获取角色、道具、场景列表
  const getCharacters = () => assets.filter(a => a.type === '角色');
  const getProps = () => assets.filter(a => a.type === '道具');
  const getScenes = () => assets.filter(a => a.type === '场景');

  // 过滤当前集的分镜
  const getFilteredShots = () => {
    if (activeEpisode === 'all') {
      return shots;
    }
    return shots.filter(shot => shot.episode === activeEpisode);
  };

  // 更新分镜字段
  const updateShot = async (shotIndex, field, value) => {
    try {
      const updatedShots = shots.map(shot => {
        if (shot.index === shotIndex) {
          return { ...shot, [field]: value };
        }
        return shot;
      });

      // 保存到服务器
      await shotApi.save(id, updatedShots);
      setShots(updatedShots);
    } catch (error) {
      message.error('更新失败');
    }
  };

  // 显示视频生成确认对话框
  const showVideoConfirm = (shot) => {
    setCurrentShot(shot);
    // 使用 video_prompt 字段（视频生成提示词）
    const videoPrompt = shot.video_prompt || shot.prompt || '';
    // 计算视频时长：优先使用分镜组时长，其次是分镜时长
    const groupDuration = calculateGroupDuration(shot.group_id);
    const videoDuration = groupDuration > 0 ? groupDuration : (shot.duration || 5);
    // 使用项目的画面比例设置，默认 16:9
    const aspectRatio = project?.video_ratio || '16:9';
    // 预填充当前分镜涉及的资产作为参考
    const preselectedIds = [];
    const shotChars = (shot.involved_characters && typeof shot.involved_characters === 'string') ? shot.involved_characters.split(',').filter(Boolean) : [];
    const shotProps = (shot.involved_props && typeof shot.involved_props === 'string') ? shot.involved_props.split(',').filter(Boolean) : [];
    const shotScenes = (shot.involved_scenes && typeof shot.involved_scenes === 'string') ? shot.involved_scenes.split(',').filter(Boolean) : [];
    assets.forEach(asset => {
      if ((asset.type === '角色' && shotChars.includes(asset.name)) ||
          (asset.type === '道具' && shotProps.includes(asset.name)) ||
          (asset.type === '场景' && shotScenes.includes(asset.name))) {
        preselectedIds.push(asset.id);
      }
    });
    setVideoConfig({
      prompt: videoPrompt,
      aspectRatio: aspectRatio,
      duration: String(videoDuration),
      resolution: '720P',
      referenceSource: preselectedIds.length > 0 ? 'asset' : 'none',
      referenceImage: null,
      referenceAssetIds: preselectedIds,
      referenceVideoUrl: '',
      referenceAudioUrl: ''
    });
    setVideoConfirmVisible(true);
  };

  // 开始生成视频
  const startGenerateVideo = async () => {
    if (!currentShot) {
      message.error('未选择分镜');
      return;
    }

    if (!videoConfig.prompt) {
      message.error('提示词不能为空');
      return;
    }

    try {
      message.loading('正在提交视频生成任务...', 0);

      // 准备请求数据
      const requestData = {
        shot_id: currentShot.id,
        project_id: parseInt(id),
        prompt: videoConfig.prompt,
        ratio: videoConfig.aspectRatio,
        duration: parseInt(videoConfig.duration) || 5,
        resolution: videoConfig.resolution
      };

      // 添加参考图（根据来源类型）
      if (videoConfig.referenceSource === 'asset' && videoConfig.referenceAssetIds && videoConfig.referenceAssetIds.length > 0) {
        requestData.reference_asset_ids = videoConfig.referenceAssetIds;
        if (videoConfig.referenceAssetIds.length === 1) {
          requestData.reference_asset_id = videoConfig.referenceAssetIds[0];
        }
      }

      // 如果有本地参考图上传
      if (videoConfig.referenceSource === 'local' && videoConfig.referenceImage) {
        // 本地图片需要通过上传接口处理，这里暂时只传递资产库选择的参考图
        console.log('[startGenerateVideo] 本地参考图:', videoConfig.referenceImage.name);
      }

      console.log('[startGenerateVideo] 请求数据:', requestData);

      // 调用API创建视频生成任务
      const response = await aiApi.createVideoGeneration(requestData);
      console.log('[startGenerateVideo] API响应:', response.data);

      message.destroy();

      if (response.data.success) {
        message.success('视频生成任务已提交');
        const taskId = response.data.task_id;

        // 更新分镜状态为生成中
        const updatedShots = shots.map(shot => {
          if (shot.id === currentShot.id || shot.index === currentShot.index) {
            return {
              ...shot,
              video_task_id: taskId,
              video_status: 'generating'
            };
          }
          return shot;
        });
        setShots(updatedShots);

        // 关闭对话框并开始轮询
        setVideoConfirmVisible(false);
        startPollingVideoStatus(taskId, currentShot.index);
      } else {
        message.error(response.data.error || '视频生成任务提交失败');
      }
    } catch (error) {
      message.destroy();
      console.error('[startGenerateVideo] 错误:', error);
      message.error('视频生成任务提交失败: ' + (error.response?.data?.error || error.message));
    }
  };

  // 轮询视频生成状态
  const startPollingVideoStatus = async (taskId, shotIndex) => {
    const pollInterval = 3000; // 3秒轮询一次
    const maxPolls = 60; // 最多轮询60次（约3分钟）
    let pollCount = 0;

    const poll = async () => {
      try {
        const response = await aiApi.getVideoGenerationStatus(taskId);
        const data = response.data;

        console.log(`[轮询] 第${pollCount + 1}次:`, data.status);

        if (data.status === 'completed' || data.status === 'succeeded' || data.status === 'success') {
          // 视频生成成功
          const updatedShots = shots.map(shot => {
            if (shot.index === shotIndex) {
              return {
                ...shot,
                video_status: 'completed',
                video_path: data.video_path || shot.video_path
              };
            }
            return shot;
          });
          setShots(updatedShots);
          message.success('视频生成成功！');
          return;
        } else if (data.status === 'failed' || data.status === 'error') {
          const updatedShots = shots.map(shot => {
            if (shot.index === shotIndex) {
              return { ...shot, video_status: 'failed' };
            }
            return shot;
          });
          setShots(updatedShots);
          message.error('视频生成失败');
          return;
        }

        // 继续轮询
        pollCount++;
        if (pollCount < maxPolls) {
          setTimeout(poll, pollInterval);
        } else {
          message.warning('视频生成超时，请稍后查看状态');
        }
      } catch (error) {
        console.error('[轮询] 错误:', error);
        pollCount++;
        if (pollCount < maxPolls) {
          setTimeout(poll, pollInterval);
        }
      }
    };

    setTimeout(poll, pollInterval);
  };

  // 处理参考图上传
  const handleReferenceImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setVideoConfig({ ...videoConfig, referenceImage: file });
    }
  };

  // 构建视频URL
  // 构建图片URL
  const getFullImageUrl = (imagePath) => {
    if (!imagePath || typeof imagePath !== 'string') return '';
    // 如果是完整的URL，使用后端代理
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return `/shortdrama/api/ai/proxy-image?url=${encodeURIComponent(imagePath)}`;
    }
    // 如果是 data/ 开头的路径，需要添加 /shortdrama 前缀
    if (imagePath.startsWith('data/')) {
      try {
        return '/shortdrama/' + imagePath.split('/').map(encodeURIComponent).join('/');
      } catch (e) {
        return '/shortdrama/' + imagePath;
      }
    }
    // 如果已经有 /shortdrama 前缀
    if (imagePath.startsWith('/shortdrama/')) {
      return imagePath;
    }
    // 其他相对路径
    let path = imagePath;
    if (path.startsWith('/')) {
      path = path.substring(1);
    }
    return '/' + path;
  };

  const buildVideoUrl = (videoPath) => {
    if (!videoPath || typeof videoPath !== 'string') return '';
    // 如果是完整的URL，直接返回
    if (videoPath.startsWith('http://') || videoPath.startsWith('https://')) {
      return videoPath;
    }
    // 如果是 data/ 开头的路径，需要添加 /shortdrama 前缀
    if (videoPath.startsWith('data/')) {
      try {
        return '/shortdrama/' + videoPath.split('/').map(encodeURIComponent).join('/');
      } catch (e) {
        return '/shortdrama/' + videoPath;
      }
    }
    // 如果已经有 /shortdrama 前缀
    if (videoPath.startsWith('/shortdrama/')) {
      try {
        const parts = videoPath.replace('/shortdrama/', '').split('/');
        return '/shortdrama/' + parts.map(encodeURIComponent).join('/');
      } catch (e) {
        return videoPath;
      }
    }
    // 去掉开头的 / 避免重复
    let path = videoPath;
    if (path.startsWith('/')) {
      path = path.substring(1);
    }
    return '/' + path;
  };

  // 处理视频上传
  const handleVideoUpload = async (file, groupId) => {
    try {
      const formData = new FormData();
      formData.append('video', file);
      formData.append('group_id', groupId);
      formData.append('project_id', id);

      const response = await api.post('/upload/video', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        message.success('视频上传成功');
        // 更新分镜组的视频路径
        const updatedShots = shots.map(shot => {
          if (shot.group_id === groupId) {
            return { ...shot, video_path: response.data.video_path };
          }
          return shot;
        });
        setShots(updatedShots);
        
        // 保存到数据库
        try {
          await shotApi.save(id, updatedShots);
          message.success('视频路径已保存到数据库');
        } catch (error) {
          message.error('保存视频路径失败: ' + (error.response?.data?.message || error.message));
        }
      } else {
        message.error('视频上传失败: ' + response.data.message);
      }
    } catch (error) {
      message.error('视频上传失败: ' + (error.response?.data?.message || error.message));
    }
  };

  // 导出Excel
  const handleExportExcel = () => {
    message.info('导出功能开发中...');
  };

  // 分镜组规划任务状态轮询
  const pollShotGroupsTaskStatus = useCallback(async (taskId) => {
    try {
      const response = await api.get(`/generate-shot-groups/status/${taskId}`);
      const { status, progress, result, error } = response.data;
      
      setShotGroupsProgress(progress);
      
      if (status === 'completed') {
        if (shotGroupsIntervalRef.current) {
          clearInterval(shotGroupsIntervalRef.current);
          shotGroupsIntervalRef.current = null;
        }
        setIsShotGroupsModalVisible(false);
        setIsGeneratingShotGroups(false);
        
        if (result && result.shot_groups) {
          await shotApi.save(id, result.shot_groups);
          message.success(`分镜组规划完成！共生成 ${result.shot_groups.length} 个分镜组`);
          loadData();
        }
      } else if (status === 'failed') {
        if (shotGroupsIntervalRef.current) {
          clearInterval(shotGroupsIntervalRef.current);
          shotGroupsIntervalRef.current = null;
        }
        setIsShotGroupsModalVisible(false);
        setIsGeneratingShotGroups(false);
        
        // 检查是否是AI未配置错误或额度用完错误
        if (error && (error.includes('AI未配置') || error.includes('API密钥') || error.includes('FreeTierOnly') || error.includes('AllocationQuota') || error.includes('分析结果为空'))) {
          Modal.error({
            title: 'AI配置或额度问题',
            content: (
              <div>
                <p>{error}</p>
                <p>请前往「项目设置」页面配置大模型API密钥或检查额度后再试。</p>
              </div>
            ),
            okText: '去配置',
            onOk: () => {
              navigate(`/project/${id}/settings`);
            }
          });
        } else {
          message.error('分镜组规划失败: ' + (error || '未知错误'));
        }
      }
    } catch (error) {
      console.error('获取分镜组任务状态失败:', error);
    }
  }, [id, navigate, loadData]);

  // 检查是否存在分镜组
  const checkExistingShotGroups = async () => {
    try {
      const response = await api.get(`/shots/project/${id}/groups`);
      return response.data && response.data.length > 0;
    } catch (error) {
      console.error('检查分镜组失败:', error);
      return false;
    }
  };

  // 分镜组规划
  const handleGenerateShotGroups = async () => {
    // 检查是否已存在分镜组
    const hasShotGroups = await checkExistingShotGroups();
    
    if (hasShotGroups) {
      Modal.confirm({
        title: '确认操作',
        content: '数据库里已经存在分镜组，是否重新执行分镜组规划命令？您的这个命令将会覆盖之前的数据。',
        okText: '确认执行',
        cancelText: '取消',
        onOk: async () => {
          setIsGeneratingShotGroups(true);
          setShotGroupsProgress({ current: 0, total: 0, episode_title: '准备中...' });
          setIsShotGroupsModalVisible(true);
          
          try {
            const startResponse = await api.post('/generate-shot-groups/start', { 
              project_id: id
            });
            
            const { task_id, total_episodes } = startResponse.data;
            setShotGroupsProgress({ current: 0, total: total_episodes, episode_title: '准备中...' });
            
            if (shotGroupsIntervalRef.current) {
              clearInterval(shotGroupsIntervalRef.current);
            }
            
            shotGroupsIntervalRef.current = setInterval(() => {
              pollShotGroupsTaskStatus(task_id);
            }, 1000);
            
          } catch (error) {
            if (shotGroupsIntervalRef.current) {
              clearInterval(shotGroupsIntervalRef.current);
              shotGroupsIntervalRef.current = null;
            }
            setIsShotGroupsModalVisible(false);
            setIsGeneratingShotGroups(false);
            const errorMsg = error.response?.data?.detail || error.message;
            message.error('启动分镜组规划失败: ' + errorMsg);
          }
        }
      });
    } else {
      setIsGeneratingShotGroups(true);
      setShotGroupsProgress({ current: 0, total: 0, episode_title: '准备中...' });
      setIsShotGroupsModalVisible(true);
      
      try {
        const startResponse = await api.post('/generate-shot-groups/start', { 
          project_id: id
        });
        
        const { task_id, total_episodes } = startResponse.data;
        setShotGroupsProgress({ current: 0, total: total_episodes, episode_title: '准备中...' });
        
        if (shotGroupsIntervalRef.current) {
          clearInterval(shotGroupsIntervalRef.current);
        }
        
        shotGroupsIntervalRef.current = setInterval(() => {
          pollShotGroupsTaskStatus(task_id);
        }, 1000);
        
      } catch (error) {
        if (shotGroupsIntervalRef.current) {
          clearInterval(shotGroupsIntervalRef.current);
          shotGroupsIntervalRef.current = null;
        }
        setIsShotGroupsModalVisible(false);
        setIsGeneratingShotGroups(false);
        const errorMsg = error.response?.data?.detail || error.message;
        message.error('启动分镜组规划失败: ' + errorMsg);
      }
    }
  };

  // 批量删除分镜组（只删除分镜组数据，不影响分镜）
  const handleBatchDelete = () => {
    if (selectedGroups.length === 0) {
      message.warning('请先选择要删除的分镜组');
      return;
    }

    Modal.confirm({
      title: '确认删除',
      content: `确定要删除选中的 ${selectedGroups.length} 个分镜组吗？分镜数据将保留，仅删除分镜组规划。`,
      okText: '删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          // 清除选中分镜组的 group_id 和 group_order
          const updatedShots = shots.map(shot => {
            if (selectedGroups.includes(shot.group_id)) {
              return {
                ...shot,
                group_id: 0,
                group_order: 0,
                group_duration: 0,
                video_prompt: ''
              };
            }
            return shot;
          });
          // 保存到服务器
          await shotApi.save(id, updatedShots);
          setShots(updatedShots);
          setSelectedGroups([]);
          message.success(`成功删除 ${selectedGroups.length} 个分镜组`);
        } catch (error) {
          message.error('删除失败: ' + (error.response?.data?.error || error.message));
        }
      }
    });
  };

  // 获取分镜组的颜色
  const getGroupColor = (groupId) => {
    const colors = [
      '#e6f7ff', '#fff7e6', '#f6ffed', '#fff0f6', '#f9f0ff',
      '#e6fffb', '#fff2e8', '#fcffe6', '#f0f5ff', '#fff1f0'
    ];
    if (!groupId || groupId === 0) return 'white';
    return colors[(groupId - 1) % colors.length];
  };

  // 计算分镜组的总时长
  const calculateGroupDuration = (groupId) => {
    if (!groupId || groupId === 0) return 0;
    const groupShots = shots.filter(s => s.group_id === groupId);
    return groupShots.reduce((sum, shot) => sum + (shot.duration || 3), 0);
  };

  // 更新分镜时长并重新计算组时长
  const updateShotDuration = async (shotIndex, newDuration) => {
    try {
      const updatedShots = shots.map(shot => {
        if (shot.index === shotIndex) {
          return { ...shot, duration: newDuration };
        }
        return shot;
      });

      // 保存到服务器
      await shotApi.save(id, updatedShots);
      setShots(updatedShots);
    } catch (error) {
      message.error('更新时长失败');
    }
  };

  // 准备表格数据，计算每个分镜的 rowSpan
  const prepareTableData = (shots) => {
    const groupInfo = {};
    shots.forEach((shot) => {
      const groupId = shot.group_id || 0;
      if (!groupInfo[groupId]) {
        groupInfo[groupId] = { count: 0, firstShot: shot };
      }
      groupInfo[groupId].count++;
    });

    return shots.map((shot) => {
      const groupId = shot.group_id || 0;
      const isFirstInGroup = shot.group_order === 0;
      const rowSpan = isFirstInGroup ? groupInfo[groupId].count : 0;
      
      return {
        ...shot,
        rowSpan,
        groupTotalDuration: calculateGroupDuration(groupId)
      };
    });
  };

  // 智能分页：确保分镜组不跨页
  const paginateShots = (shots, currentPage, pageSize) => {
    if (!shots || shots.length === 0) {
      return { paginatedShots: [], total: 0, pageCount: 0 };
    }

    // 按分镜组组织数据
    const groups = [];
    let currentGroup = null;
    
    shots.forEach((shot) => {
      if (shot.group_id !== currentGroup?.groupId) {
        currentGroup = {
          groupId: shot.group_id,
          shots: []
        };
        groups.push(currentGroup);
      }
      currentGroup.shots.push(shot);
    });

    // 计算每页显示哪些分镜组
    const pages = [];
    let currentPageShots = [];
    let currentPageSize = 0;

    groups.forEach((group) => {
      const groupSize = group.shots.length;
      
      // 如果当前页加上这个分镜组会超过 pageSize，且当前页已有内容，则新开一页
      if (currentPageSize + groupSize > pageSize && currentPageSize > 0) {
        pages.push(currentPageShots);
        currentPageShots = [...group.shots];
        currentPageSize = groupSize;
      } else {
        // 否则加入当前页
        currentPageShots = [...currentPageShots, ...group.shots];
        currentPageSize += groupSize;
      }
    });

    // 添加最后一页
    if (currentPageShots.length > 0) {
      pages.push(currentPageShots);
    }

    const pageIndex = Math.min(currentPage - 1, pages.length - 1);
    const paginatedShots = pages[pageIndex] || [];

    return {
      paginatedShots,
      total: shots.length,
      pageCount: pages.length
    };
  };

  // 表格列定义
  const columns = [
    {
      title: '分镜组',
      dataIndex: 'group_id',
      key: 'group_id',
      width: 100,
      render: (groupId, record) => {
        // 只在分镜组的第一行显示选择框和组号
        if (record.rowSpan === 0) {
          return { props: { rowSpan: 0 } };
        }
        
        const isSelected = selectedGroups.includes(groupId);
        
        const obj = {
          children: (
            <div style={{ textAlign: 'center' }}>
              {groupId && groupId > 0 ? (
                <>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedGroups([...selectedGroups, groupId]);
                      } else {
                        setSelectedGroups(selectedGroups.filter(id => id !== groupId));
                      }
                    }}
                    style={{ cursor: 'pointer', width: 16, height: 16, marginBottom: 4 }}
                  />
                  <div style={{ 
                    fontWeight: 'bold', 
                    fontSize: 16,
                    color: '#1890ff'
                  }}>
                    {groupId}
                  </div>
                </>
              ) : (
                <div style={{ 
                  fontWeight: 'bold', 
                  fontSize: 16,
                  color: '#999'
                }}>
                  -
                </div>
              )}
            </div>
          ),
          props: { rowSpan: record.rowSpan }
        };
        return obj;
      },
    },
    {
      title: '序号',
      dataIndex: 'index',
      key: 'index',
      width: 60,
      render: (index, record) => (
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontWeight: 'bold' }}>{index}</div>
          <div style={{ fontSize: 11, color: '#999' }}>{record.episode}</div>
        </div>
      ),
    },
    {
      title: '分镜详情',
      key: 'shot_details',
      width: 350,
      render: (_, record) => (
        <div>
          <div style={{ marginBottom: 4 }}>
            <Tag size="small" color="blue">{record.scene}</Tag>
            <Tag size="small">{record.camera}</Tag>
            <Tag size="small">{record.movement}</Tag>
          </div>
          <div style={{ fontSize: 13, marginBottom: 4 }}>
            <strong>角色：</strong>{record.characters || '-'}
          </div>
          <div style={{ fontSize: 13, marginBottom: 4 }}>
            <strong>动作：</strong>{record.action || '-'}
          </div>
          {record.dialog && (
            <div style={{ fontSize: 13, marginBottom: 4, color: '#666' }}>
              <strong>台词：</strong>{record.dialog}
            </div>
          )}
          {record.sound && (
            <div style={{ fontSize: 12, color: '#999' }}>
              <strong>音效：</strong>{record.sound}
            </div>
          )}
        </div>
      ),
    },
    {
      title: '分镜时长(秒)',
      dataIndex: 'duration',
      key: 'duration',
      width: 90,
      render: (duration, record) => (
        <Select
          value={duration || 3}
          onChange={(value) => updateShotDuration(record.index, value)}
          style={{ width: 60 }}
          size="small"
        >
          {Array.from({ length: 15 }, (_, i) => i + 1).map(sec => (
            <Option key={sec} value={sec}>{sec}</Option>
          ))}
        </Select>
      ),
    },
    {
      title: '分镜组时长(秒)',
      dataIndex: 'groupTotalDuration',
      key: 'group_duration_display',
      width: 90,
      render: (value, record) => {
        const obj = {
          children: (
            <div style={{ textAlign: 'center', fontWeight: 'bold', color: '#1890ff' }}>
              {record.group_id ? `${value}秒` : '-'}
            </div>
          ),
          props: {}
        };
        if (record.rowSpan > 0) {
          obj.props.rowSpan = record.rowSpan;
        } else {
          obj.props.rowSpan = 0;
        }
        return obj;
      },
    },
    {
      title: '视频生成提示词',
      dataIndex: 'video_prompt',
      key: 'video_prompt',
      width: 350,
      render: (video_prompt, record) => {
        // 更新分镜组的视频提示词
        const updateVideoPrompt = async (newPrompt) => {
          try {
            const groupId = record.group_id;
            // 更新该分镜组所有分镜的 video_prompt
            const updatedShots = shots.map(shot => {
              if (shot.group_id === groupId) {
                return { ...shot, video_prompt: newPrompt };
              }
              return shot;
            });
            // 保存到服务器
            await shotApi.save(id, updatedShots);
            setShots(updatedShots);
            message.success('视频提示词已更新');
          } catch (error) {
            message.error('更新失败');
          }
        };

        // 计算文本框高度：每个分镜约100px高度
        const textareaHeight = record.rowSpan * 100;
        
        const obj = {
          children: (
            <div style={{ 
              height: textareaHeight, 
              minHeight: 300,
              display: 'flex',
              flexDirection: 'column'
            }}>
              <textarea
                value={video_prompt || ''}
                onChange={(e) => {
                  // 本地先更新显示
                  const groupId = record.group_id;
                  const updatedShots = shots.map(shot => {
                    if (shot.group_id === groupId) {
                      return { ...shot, video_prompt: e.target.value };
                    }
                    return shot;
                  });
                  setShots(updatedShots);
                }}
                onBlur={(e) => {
                  // 失去焦点时保存
                  updateVideoPrompt(e.target.value);
                }}
                style={{
                  width: '100%',
                  height: '100%',
                  minHeight: 300,
                  fontSize: 12,
                  lineHeight: 1.5,
                  padding: 8,
                  border: '1px solid #d9d9d9',
                  borderRadius: 4,
                  resize: 'none',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box'
                }}
                placeholder="请输入视频生成提示词..."
              />
            </div>
          ),
          props: {}
        };
        if (record.rowSpan > 0) {
          obj.props.rowSpan = record.rowSpan;
        } else {
          obj.props.rowSpan = 0;
        }
        return obj;
      },
    },
    {
      title: '涉及角色',
      dataIndex: 'involved_characters',
      key: 'involved_characters',
      width: 110,
      render: (value, record) => {
        const obj = {
          children: (
            <Select
              mode="multiple"
              placeholder="选择角色"
              value={value ? value.split(',').filter(Boolean) : []}
              onChange={(selected) => updateShot(record.index, 'involved_characters', selected.join(','))}
              style={{ width: '100%' }}
              size="small"
            >
              {getCharacters().map(char => (
                <Option key={char.name} value={char.name}>{char.name}</Option>
              ))}
            </Select>
          ),
          props: {}
        };
        if (record.rowSpan > 0) {
          obj.props.rowSpan = record.rowSpan;
        } else {
          obj.props.rowSpan = 0;
        }
        return obj;
      },
    },
    {
      title: '涉及道具',
      dataIndex: 'involved_props',
      key: 'involved_props',
      width: 110,
      render: (value, record) => {
        const obj = {
          children: (
            <Select
              mode="multiple"
              placeholder="选择道具"
              value={value ? value.split(',').filter(Boolean) : []}
              onChange={(selected) => updateShot(record.index, 'involved_props', selected.join(','))}
              style={{ width: '100%' }}
              size="small"
            >
              {getProps().map(prop => (
                <Option key={prop.name} value={prop.name}>{prop.name}</Option>
              ))}
            </Select>
          ),
          props: {}
        };
        if (record.rowSpan > 0) {
          obj.props.rowSpan = record.rowSpan;
        } else {
          obj.props.rowSpan = 0;
        }
        return obj;
      },
    },
    {
      title: '涉及场景',
      dataIndex: 'involved_scenes',
      key: 'involved_scenes',
      width: 110,
      render: (value, record) => {
        const obj = {
          children: (
            <Select
              placeholder="选择场景"
              value={value || undefined}
              onChange={(selected) => updateShot(record.index, 'involved_scenes', selected)}
              style={{ width: '100%' }}
              size="small"
              allowClear
            >
              {getScenes().map(scene => (
                <Option key={scene.name} value={scene.name}>{scene.name}</Option>
              ))}
            </Select>
          ),
          props: {}
        };
        if (record.rowSpan > 0) {
          obj.props.rowSpan = record.rowSpan;
        } else {
          obj.props.rowSpan = 0;
        }
        return obj;
      },
    },
    {
      title: '参考声音',
      dataIndex: 'reference_voice',
      key: 'reference_voice',
      width: 90,
      render: (value, record) => {
        const obj = {
          children: (
            <div>
              {value ? (
                <Tag color="green">已选择</Tag>
              ) : (
                <Tag color="default">无</Tag>
              )}
              <Button
                size="small"
                style={{ marginTop: 4 }}
                onClick={() => message.info('声音选择功能开发中...')}
              >
                选择
              </Button>
            </div>
          ),
          props: {}
        };
        if (record.rowSpan > 0) {
          obj.props.rowSpan = record.rowSpan;
        } else {
          obj.props.rowSpan = 0;
        }
        return obj;
      },
    },
    {
      title: '视频',
      dataIndex: 'video_path',
      key: 'video_path',
      width: 150,
      render: (value, record) => {
        const obj = {
          children: (
            <div>
              {value ? (
                <div>
                  <Tag color="blue">已上传</Tag>
                  <div style={{ marginTop: 8 }}>
                    <video
                      src={buildVideoUrl(value)}
                      controls
                      style={{
                        width: '100%',
                        maxHeight: 100,
                        borderRadius: 4
                      }}
                      poster={`${buildVideoUrl(value)}?thumbnail=1`}
                    />
                  </div>
                  <Button
                    size="small"
                    type="link"
                    icon={<DownloadOutlined />}
                    style={{ marginTop: 4 }}
                  >
                    下载
                  </Button>
                </div>
              ) : (
                <div>
                  <Tag color="default">未上传</Tag>
                  <Upload
                    accept="video/*"
                    showUploadList={false}
                    beforeUpload={(file) => {
                      handleVideoUpload(file, record.group_id);
                      return false;
                    }}
                  >
                    <Button
                      size="small"
                      icon={<UploadOutlined />}
                      style={{ marginTop: 4 }}
                    >
                      上传
                    </Button>
                  </Upload>
                </div>
              )}
            </div>
          ),
          props: {}
        };
        if (record.rowSpan > 0) {
          obj.props.rowSpan = record.rowSpan;
        } else {
          obj.props.rowSpan = 0;
        }
        return obj;
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 90,
      render: (_, record) => {
        const obj = {
          children: (
            <Button
              type="primary"
              size="small"
              icon={<VideoCameraOutlined />}
              loading={generatingVideo === record.index}
              onClick={() => showVideoConfirm(record)}
            >
              生成
            </Button>
          ),
          props: {}
        };
        if (record.rowSpan > 0) {
          obj.props.rowSpan = record.rowSpan;
        } else {
          obj.props.rowSpan = 0;
        }
        return obj;
      },
    },
  ];

  // 集数卡片 - 显示分镜组数量
  const renderEpisodeCards = () => {
    if (episodes.length === 0) return null;

    return (
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {episodes.map(episode => {
            const episodeShots = shots.filter(s => s.episode === episode);
            // 计算该集的分镜组数量（按 group_id 去重，且只统计有 group_id 的分镜）
            const groupIds = new Set(episodeShots.filter(s => s.group_id && s.group_id > 0).map(s => s.group_id));
            const groupCount = groupIds.size;
            return (
              <Button
                key={episode}
                type={activeEpisode === episode ? 'primary' : 'default'}
                onClick={() => setActiveEpisode(episode)}
              >
                {episode} ({groupCount}个分镜组)
              </Button>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div>
      <Button 
        icon={<ArrowLeftOutlined />} 
        onClick={() => navigate(`/project/${id}`)}
        style={{ marginBottom: 16 }}
      >
        返回项目管理
      </Button>

      <Card
        title="制作视频"
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={loadData}>
              刷新
            </Button>
            <Button
              icon={<ImportOutlined />}
              onClick={() => setIsImportGroupsModalVisible(true)}
            >
              导入分镜组
            </Button>
            <Button
              icon={<ExportOutlined />}
              onClick={() => setIsExportGroupsModalVisible(true)}
              disabled={!shots.some(s => s.group_id && s.group_id > 0)}
            >
              导出分镜组
            </Button>
            <Button
              type="primary"
              icon={<ClusterOutlined />}
              onClick={handleGenerateShotGroups}
              loading={isGeneratingShotGroups}
            >
              分镜组规划
            </Button>

          </Space>
        }
      >
        {renderEpisodeCards()}



        {(() => {
          const filteredShots = getFilteredShots();
          // 检查是否有分镜组数据
          const hasGroups = filteredShots.some(shot => shot.group_id && shot.group_id > 0);
          
          if (!hasGroups) {
            return (
              <div style={{ 
                textAlign: 'center', 
                padding: '60px 20px',
                color: '#999',
                background: '#f5f5f5',
                borderRadius: 8,
                marginTop: 16
              }}>
                <div style={{ fontSize: 16, marginBottom: 8 }}>暂无分镜组数据</div>
                <div style={{ fontSize: 14 }}>请先在"分镜生成"页面生成分镜，然后点击"生成分镜组"按钮创建分镜组</div>
              </div>
            );
          }
          
          const { paginatedShots, total, pageCount } = paginateShots(
            filteredShots,
            pagination.current,
            pagination.pageSize
          );

          // 计算用于分页组件的 total，确保显示正确的页数
          const paginationTotal = pageCount * pagination.pageSize;

          return (
            <>
              <Table
                columns={columns}
                dataSource={prepareTableData(paginatedShots)}
                rowKey="index"
                loading={loading}
                pagination={false}
                scroll={{ x: 1500 }}
                size="small"
                bordered
                rowClassName={(record) => `group-row-${record.group_id || 0}`}
                onRow={(record) => ({
                  style: {
                    backgroundColor: getGroupColor(record.group_id)
                  }
                })}

              />
              <div style={{ marginTop: 16, textAlign: 'right' }}>
                <Pagination
                  current={pagination.current}
                  total={paginationTotal}
                  pageSize={pagination.pageSize}
                  onChange={(page) => {
                    // 确保页码不超过实际页数
                    const validPage = Math.min(page, pageCount);
                    setPagination({ ...pagination, current: validPage });
                  }}
                  showTotal={() => `共 ${total} 条，${pageCount} 页`}
                  // 禁用页码大小调整
                  showSizeChanger={false}
                  // 显示快速跳转
                  showQuickJumper
                />
              </div>
            </>
          );
        })()}
      </Card>

      {(() => {
        const hasGroups = shots.some(shot => shot.group_id && shot.group_id > 0);
        if (!hasGroups) return null;
        return (
          <Card title="批量操作" style={{ marginTop: 16 }}>
            <Space>
              <Button
                type="primary"
                icon={<PlayCircleOutlined />}
                onClick={() => message.info('批量生成功能开发中...')}
              >
                批量生成视频
              </Button>
              <Button
                onClick={() => message.info('合成功能开发中...')}
              >
                合成完整视频
              </Button>
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={handleBatchDelete}
                disabled={selectedGroups.length === 0}
              >
                批量删除分镜组{selectedGroups.length > 0 ? ` (${selectedGroups.length})` : ''}
              </Button>
            </Space>
          </Card>
        );
      })()}

      {/* 视频生成确认对话框 */}
      <Modal
        title="生成视频配置"
        visible={videoConfirmVisible}
        onCancel={() => setVideoConfirmVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setVideoConfirmVisible(false)}>
            取消
          </Button>,
          <Button key="submit" type="primary" onClick={startGenerateVideo}>
            开始生成
          </Button>,
        ]}
        width={600}
      >
        <div style={{ padding: '10px 0' }}>
          {/* 提示词预览 */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontWeight: 'bold', marginBottom: 8, fontSize: 14 }}>提示词</div>
            <div style={{
              background: '#f5f5f5',
              padding: 12,
              borderRadius: 4,
              fontSize: 13,
              whiteSpace: 'pre-wrap',
              maxHeight: 200,
              overflow: 'auto',
              lineHeight: 1.6
            }}>
              {videoConfig.prompt}
            </div>
          </div>

          {/* 画面比例 */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontWeight: 'bold', marginBottom: 8, fontSize: 14 }}>画面比例</div>
            <Select
              value={videoConfig.aspectRatio}
              onChange={(value) => setVideoConfig({ ...videoConfig, aspectRatio: value })}
              style={{ width: 200 }}
            >
              <Option value="16:9">16:9 (横屏)</Option>
              <Option value="9:16">9:16 (竖屏)</Option>
              <Option value="1:1">1:1 (方形)</Option>
              <Option value="4:3">4:3</Option>
            </Select>
          </div>

          {/* 视频时长 - 从表格中获取 */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontWeight: 'bold', marginBottom: 8, fontSize: 14 }}>视频时长</div>
            <div style={{ 
              padding: '8px 12px', 
              background: '#f5f5f5', 
              borderRadius: 4,
              width: 200,
              color: '#333'
            }}>
              {currentShot?.group_duration || currentShot?.duration || 5}秒（由分镜组时长决定）
            </div>
          </div>

          {/* 分辨率 */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontWeight: 'bold', marginBottom: 8, fontSize: 14 }}>分辨率</div>
            <Select
              value={videoConfig.resolution}
              onChange={(value) => setVideoConfig({ ...videoConfig, resolution: value })}
              style={{ width: 200 }}
            >
              <Option value="480P">480P</Option>
              <Option value="720P">720P</Option>
              <Option value="1080P">1080P</Option>
            </Select>
          </div>

          {/* 参考图选择 */}
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontWeight: 'bold', marginBottom: 8, fontSize: 14 }}>参考图（可选，支持多选）</div>
            <Radio.Group
              value={videoConfig.referenceSource}
              onChange={(e) => setVideoConfig({
                ...videoConfig,
                referenceSource: e.target.value,
                referenceAssetIds: e.target.value === 'asset' ? videoConfig.referenceAssetIds : [],
                referenceImage: e.target.value === 'local' ? videoConfig.referenceImage : null
              })}
              style={{ marginBottom: 12 }}
            >
              <Radio value="none">不使用参考图</Radio>
              <Radio value="asset">从资产库选择</Radio>
              <Radio value="local">本地图片上传</Radio>
            </Radio.Group>

            {videoConfig.referenceSource === 'asset' && (
              <div>
                {loading ? (
                  <div style={{ color: '#999' }}>加载资产中...</div>
                ) : assets.length === 0 ? (
                  <div style={{ color: '#999' }}>资产库为空，请先添加资产</div>
                ) : (
                  <>
                    <div style={{
                      maxHeight: 200,
                      overflowY: 'auto',
                      border: '1px solid #d9d9d9',
                      borderRadius: 4,
                      padding: 8
                    }}>
                      {assets.map(asset => {
                        const isSelected = videoConfig.referenceAssetIds.includes(asset.id);
                        const selectedIndex = videoConfig.referenceAssetIds.indexOf(asset.id);

                        return (
                          <div
                            key={asset.id}
                            onClick={() => {
                              if (isSelected) {
                                setVideoConfig({
                                  ...videoConfig,
                                  referenceAssetIds: videoConfig.referenceAssetIds.filter(id => id !== asset.id)
                                });
                              } else {
                                setVideoConfig({
                                  ...videoConfig,
                                  referenceAssetIds: [...videoConfig.referenceAssetIds, asset.id]
                                });
                              }
                            }}
                            style={{
                              padding: '8px 12px',
                              cursor: 'pointer',
                              borderRadius: 4,
                              marginBottom: 4,
                              background: isSelected ? '#e6f7ff' : 'transparent',
                              border: isSelected ? '1px solid #1890ff' : '1px solid transparent',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8
                            }}
                          >
                            <div style={{
                              width: 20,
                              height: 20,
                              borderRadius: 4,
                              border: '1px solid #d9d9d9',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 12,
                              fontWeight: 'bold'
                            }}>
                              {isSelected ? selectedIndex + 1 : ''}
                            </div>
                            {asset.image_path && (
                              <img
                                src={getFullImageUrl(asset.image_path)}
                                alt={asset.name}
                                style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }}
                              />
                            )}
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 'bold' }}>{asset.name}</div>
                              <div style={{ fontSize: 12, color: '#999' }}>
                                {asset.type}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {videoConfig.referenceAssetIds.length > 0 && (
                      <div style={{ marginTop: 12 }}>
                        <div style={{ color: '#52c41a', marginBottom: 4 }}>
                          已选择 {videoConfig.referenceAssetIds.length} 个资产：
                        </div>
                        <div style={{ fontSize: 12, color: '#666' }}>
                          {videoConfig.referenceAssetIds.map((assetId, idx) => {
                            const asset = assets.find(a => a.id === assetId);
                            if (!asset) return null;
                            return (
                              <span key={assetId} style={{ marginRight: 12 }}>
                                <span style={{ color: '#1890ff' }}>图{idx + 1}</span>：
                                {asset.name}（{asset.type}）
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {videoConfig.referenceSource === 'local' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleReferenceImageChange}
                  style={{ display: 'none' }}
                  id="reference-image-input"
                />
                <div style={{
                  flex: 1,
                  padding: '8px 12px',
                  background: videoConfig.referenceImage ? '#e6f7ff' : '#f5f5f5',
                  borderRadius: 4,
                  color: videoConfig.referenceImage ? '#333' : '#999',
                  fontSize: 13
                }}>
                  {videoConfig.referenceImage ? videoConfig.referenceImage.name : '选择本地图片作为参考图...'}
                </div>
                <Button onClick={() => document.getElementById('reference-image-input').click()}>
                  浏览...
                </Button>
              </div>
            )}
          </div>
        </div>
      </Modal>

      {/* 视频生成进度弹窗 */}
      <Modal
        title="正在生成视频..."
        visible={videoProgress.visible}
        closable={false}
        maskClosable={false}
        footer={null}
        width={400}
      >
        <div style={{ padding: '20px 0', textAlign: 'center' }}>
          <Progress
            percent={videoProgress.percent}
            status={videoProgress.percent === 100 ? 'success' : 'active'}
            strokeColor={{ from: '#108ee9', to: '#87d068' }}
          />
          <div style={{ marginTop: 16, fontSize: 14, color: '#666' }}>
            {videoProgress.status}
          </div>
        </div>
      </Modal>

      {/* 导入分镜组模态框 */}
      <Modal
        title="导入分镜组"
        open={isImportGroupsModalVisible}
        onCancel={() => {
          setIsImportGroupsModalVisible(false);
          setImportGroupsFile(null);
        }}
        footer={null}
      >
        <div style={{ padding: '20px 0' }}>
          <div style={{ marginBottom: 16 }}>
            <p style={{ color: '#666', marginBottom: 16 }}>
              支持导入 JSON 格式或 Excel 格式的分镜组文件。
            </p>
            <p style={{ color: '#999', fontSize: 12 }}>
              Excel 格式示例（列名）：<br/>
              序号、集数、场景、提示词、涉及角色、涉及道具、涉及场景、分镜数量、总时长(秒)
            </p>
          </div>
          
          <Upload
            accept=".json,.xlsx,.xls"
            beforeUpload={(file) => {
              setImportGroupsFile(file);
              return false;
            }}
            onRemove={() => setImportGroupsFile(null)}
            maxCount={1}
          >
            <Button icon={<UploadOutlined />}>选择文件</Button>
          </Upload>
          
          {importGroupsFile && (
            <div style={{ marginTop: 16, padding: 12, background: '#f6ffed', borderRadius: 4 }}>
              <span style={{ color: '#52c41a' }}>已选择: {importGroupsFile.name}</span>
            </div>
          )}
          
          <Space style={{ width: '100%', justifyContent: 'flex-end', marginTop: 24 }}>
            <Button onClick={() => {
              setIsImportGroupsModalVisible(false);
              setImportGroupsFile(null);
            }}>
              取消
            </Button>
            <Button 
              type="primary" 
              icon={<ImportOutlined />}
              disabled={!importGroupsFile}
              onClick={async () => {
                if (!importGroupsFile) {
                  message.warning('请先选择文件');
                  return;
                }
                
                try {
                  const fileExtension = importGroupsFile.name.split('.').pop().toLowerCase();
                  let groupsData = [];
                  
                  if (fileExtension === 'json') {
                    const content = await importGroupsFile.text();
                    groupsData = JSON.parse(content);
                  } else if (fileExtension === 'xlsx' || fileExtension === 'xls') {
                    const XLSX = await import('xlsx');
                    const data = await importGroupsFile.arrayBuffer();
                    const workbook = XLSX.read(data, { type: 'array' });
                    const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
                    groupsData = XLSX.utils.sheet_to_json(firstSheet);
                  } else {
                    message.error('不支持的文件格式，请上传 JSON 或 Excel 文件');
                    return;
                  }
                  
                  if (!Array.isArray(groupsData)) {
                    message.error('文件格式错误：数据必须是数组');
                    return;
                  }
                  
                  message.success(`成功导入 ${groupsData.length} 个分镜组信息`);
                  setIsImportGroupsModalVisible(false);
                  setImportGroupsFile(null);
                } catch (error) {
                  message.error('导入失败: ' + error.message);
                }
              }}
            >
              开始导入
            </Button>
          </Space>
        </div>
      </Modal>

      {/* 导出分镜组模态框 */}
      <Modal
        title="导出分镜组"
        open={isExportGroupsModalVisible}
        onCancel={() => setIsExportGroupsModalVisible(false)}
        footer={null}
      >
        <div style={{ padding: '20px 0' }}>
          <p style={{ color: '#666', marginBottom: 24 }}>
            当前共有 <strong>{shots.filter(s => s.group_id && s.group_id > 0).length}</strong> 个分镜组分镜，请选择导出格式：
          </p>
          
          <Space direction="vertical" style={{ width: '100%' }}>
            <Button 
              icon={<FileTextOutlined />} 
              size="large"
              style={{ width: '100%', textAlign: 'left' }}
              onClick={() => {
                // 按分镜组整理数据
                const groupsMap = new Map();
                shots.forEach(shot => {
                  if (shot.group_id && shot.group_id > 0) {
                    if (!groupsMap.has(shot.group_id)) {
                      groupsMap.set(shot.group_id, {
                        group_id: shot.group_id,
                        episode: shot.episode,
                        scene: shot.scene,
                        prompt: shot.video_prompt || shot.prompt,
                        involved_characters: shot.involved_characters,
                        involved_props: shot.involved_props,
                        involved_scenes: shot.involved_scenes,
                        shots: []
                      });
                    }
                    groupsMap.get(shot.group_id).shots.push({
                      index: shot.index,
                      action: shot.action,
                      duration: shot.duration
                    });
                  }
                });
                
                const exportData = Array.from(groupsMap.values()).map((group, idx) => ({
                  '序号': idx + 1,
                  '集数': group.episode,
                  '场景': group.scene,
                  '提示词': group.prompt,
                  '涉及角色': group.involved_characters,
                  '涉及道具': group.involved_props,
                  '涉及场景': group.involved_scenes,
                  '分镜数量': group.shots.length,
                  '总时长(秒)': group.shots.reduce((sum, s) => sum + (s.duration || 3), 0)
                }));
                
                const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `shot_groups_${id}_${new Date().toISOString().slice(0, 10)}.json`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
                
                message.success('JSON 格式导出成功');
                setIsExportGroupsModalVisible(false);
              }}
            >
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontWeight: 'bold' }}>导出为 JSON 格式</div>
                <div style={{ fontSize: 12, color: '#999' }}>适合程序处理，包含完整数据</div>
              </div>
            </Button>
            
            <Button 
              icon={<FileExcelOutlined />} 
              size="large"
              style={{ width: '100%', textAlign: 'left' }}
              onClick={async () => {
                try {
                  const XLSX = await import('xlsx');
                  
                  // 按分镜组整理数据
                  const groupsMap = new Map();
                  shots.forEach(shot => {
                    if (shot.group_id && shot.group_id > 0) {
                      if (!groupsMap.has(shot.group_id)) {
                        groupsMap.set(shot.group_id, {
                          group_id: shot.group_id,
                          episode: shot.episode,
                          scene: shot.scene,
                          prompt: shot.video_prompt || shot.prompt,
                          involved_characters: shot.involved_characters,
                          involved_props: shot.involved_props,
                          involved_scenes: shot.involved_scenes,
                          shots: []
                        });
                      }
                      groupsMap.get(shot.group_id).shots.push({
                        index: shot.index,
                        action: shot.action,
                        duration: shot.duration
                      });
                    }
                  });
                  
                  const exportData = Array.from(groupsMap.values()).map((group, idx) => ({
                    '序号': idx + 1,
                    '集数': group.episode,
                    '场景': group.scene,
                    '提示词': group.prompt,
                    '涉及角色': group.involved_characters,
                    '涉及道具': group.involved_props,
                    '涉及场景': group.involved_scenes,
                    '分镜数量': group.shots.length,
                    '总时长(秒)': group.shots.reduce((sum, s) => sum + (s.duration || 3), 0)
                  }));
                  
                  const worksheet = XLSX.utils.json_to_sheet(exportData);
                  const workbook = XLSX.utils.book_new();
                  XLSX.utils.book_append_sheet(workbook, worksheet, '分镜组列表');
                  
                  XLSX.writeFile(workbook, `shot_groups_${id}_${new Date().toISOString().slice(0, 10)}.xlsx`);
                  
                  message.success('Excel 格式导出成功');
                  setIsExportGroupsModalVisible(false);
                } catch (error) {
                  message.error('导出 Excel 失败: ' + error.message);
                }
              }}
            >
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontWeight: 'bold' }}>导出为 Excel 格式</div>
                <div style={{ fontSize: 12, color: '#999' }}>适合人工查看和编辑</div>
              </div>
            </Button>
          </Space>
          
          <div style={{ marginTop: 24, textAlign: 'right' }}>
            <Button onClick={() => setIsExportGroupsModalVisible(false)}>取消</Button>
          </div>
        </div>
      </Modal>

      {/* 分镜组规划进度弹窗 */}
      <Modal
        title="正在生成分镜组规划..."
        visible={isShotGroupsModalVisible}
        closable={false}
        maskClosable={false}
        footer={null}
        width={500}
      >
        <div style={{ padding: '20px 0' }}>
          <Progress
            percent={shotGroupsProgress.total > 0 ? Math.round((shotGroupsProgress.current / shotGroupsProgress.total) * 100) : 0}
            status="active"
            strokeColor={{ from: '#1890ff', to: '#52c41a' }}
          />
          <div style={{ marginTop: 16, textAlign: 'center' }}>
            <div style={{ fontSize: 16, fontWeight: 'bold', color: '#1890ff' }}>
              {shotGroupsProgress.episode_title || '准备中...'}
            </div>
            <div style={{ marginTop: 8, color: '#666' }}>
              进度: {shotGroupsProgress.current} / {shotGroupsProgress.total} 集
            </div>
          </div>
          <div style={{ marginTop: 16, padding: 12, background: '#f5f5f5', borderRadius: 4, fontSize: 12, color: '#666' }}>
            <div>正在使用AI大模型分析剧本，生成分镜组规划...</div>
            <div style={{ marginTop: 4 }}>每个分镜组约10-15秒，每集分析大约需要10-30秒</div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ProductionPage;
